import { Box, Button, Card, Flex } from '@auviknetworks/iceberg-ui';
import { FormLabel, Typography } from '@mui/material';
import { Row, RowModel } from '@tanstack/react-table';
import { UserPicker } from 'application/Shared/Components/Pickers';
import { useManagedTableV2Context } from 'application/Shared/Components/Table';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { FixedPositionPageFooter } from './FixedPositionPageFooter';
import { RecipeWithTenantApp } from './types';

const PageFooter = () => {
    const { t } = useTranslation();
    const { table } = useManagedTableV2Context();
    const [, setSearchParams] = useSearchParams();
    const noAppsSelected = table?.getSelectedRowModel().rows.length === 0;

    const handleClick = () => {
        const { rows } =
            table.getSelectedRowModel() as RowModel<RecipeWithTenantApp>;

        const applicationIds = rows.map(
            (row: Row<RecipeWithTenantApp>) => row.original.applicationId
        );

        setSearchParams((currentParams) => {
            if (applicationIds.length) {
                currentParams.set('applicationId', applicationIds.join(','));
            }

            return currentParams;
        });
    };

    return (
        <FixedPositionPageFooter>
            <Button
                data-trackid="offboardUser-userAppsSelected-next"
                disabled={noAppsSelected}
                onClick={handleClick}
            >
                {t('Common.next')}
            </Button>
        </FixedPositionPageFooter>
    );
};

export const OffboardSelectUsers = () => {
    const { t } = useTranslation();

    const [searchParams, setSearchParams] = useSearchParams();

    const userId = searchParams.get('userId');

    const handleChange = (selectedUserId: string) => {
        setSearchParams((currentSearchParams) => {
            if (selectedUserId) {
                currentSearchParams.set('userId', selectedUserId);
            } else {
                currentSearchParams.delete('userId');
            }

            return currentSearchParams;
        });
    };

    return (
        <Card variant="outlined" data-testid="offboardUsersCard">
            <Box sx={{ margin: '16px' }}>
                <Typography
                    variant="h6"
                    color="textSecondary"
                    fontWeight={600}
                    data-testid="offboardUsersTitle"
                >
                    {t('Common.users')}
                </Typography>
                <hr />
                <Flex
                    alignItems="center"
                    display="flex"
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    <FormLabel
                        sx={{ marginRight: '60px' }}
                        component="label"
                        data-testid="offboardUsersLabel"
                    >
                        {t('Common.userEmailOrId')}
                    </FormLabel>
                    <UserPicker
                        data-testid="offboardUsersPicker"
                        hideInactiveUsersOption
                        onChange={handleChange}
                        size="sm"
                        style={{ maxWidth: '500px' }}
                        value={userId}
                        variant="outlined"
                    />
                </Flex>
                <PageFooter />
            </Box>
        </Card>
    );
};
